import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Grid, MenuItem, Paper, Snackbar, Table, TableBody,
  TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import LoyaltyService from '../../services/loyalty.service';
import LocationDataService from '../../services/location.service';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';
import { apiError, formatDateTime, formatMoney, formatPoints } from './loyaltyUtils';

const LoyaltyTransactionList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [filters, setFilters] = useState({ location_id: '', type: '', from: '', to: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    LocationDataService.getAll().then((r) => setLocations(r.data?.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: page + 1, per_page: perPage };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await LoyaltyService.getTransactions(params);
      setRows(res.data?.data || []);
      setTotal(res.data?.total || 0);
    } catch (error) {
      setSnackbar({ open: true, message: apiError(error, t('loyalty.errorLoading')), severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, perPage, filters, t]);

  useEffect(() => { load(); }, [load]);

  const refund = async (tx) => {
    if (!window.confirm(t('loyalty.refundConfirm'))) return;
    try {
      await LoyaltyService.refundTransaction(tx.uuid);
      setSnackbar({ open: true, message: t('loyalty.refunded'), severity: 'success' });
      load();
    } catch (error) {
      setSnackbar({ open: true, message: apiError(error, t('loyalty.errorSaving')), severity: 'error' });
    }
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" mb={3}>{t('loyalty.transactions')}</Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField select fullWidth label={t('loyalty.location')} value={filters.location_id} onChange={(e) => { setFilters({ ...filters, location_id: e.target.value }); setPage(0); }}>
                <MenuItem value="">{t('loyalty.all')}</MenuItem>
                {locations.map((l) => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField select fullWidth label={t('loyalty.type')} value={filters.type} onChange={(e) => { setFilters({ ...filters, type: e.target.value }); setPage(0); }}>
                <MenuItem value="">{t('loyalty.all')}</MenuItem>
                <MenuItem value="PURCHASE">PURCHASE</MenuItem>
                <MenuItem value="REFUND">REFUND</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField fullWidth type="date" label={t('loyalty.from')} InputLabelProps={{ shrink: true }} value={filters.from} onChange={(e) => { setFilters({ ...filters, from: e.target.value }); setPage(0); }} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField fullWidth type="date" label={t('loyalty.to')} InputLabelProps={{ shrink: true }} value={filters.to} onChange={(e) => { setFilters({ ...filters, to: e.target.value }); setPage(0); }} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('common.date')}</TableCell>
                <TableCell>{t('loyalty.customer')}</TableCell>
                <TableCell>{t('loyalty.type')}</TableCell>
                <TableCell>{t('loyalty.location')}</TableCell>
                <TableCell align="right">{t('common.total')}</TableCell>
                <TableCell align="right">{t('loyalty.points')}</TableCell>
                <TableCell>{t('loyalty.cashier')}</TableCell>
                <TableCell>{t('loyalty.reference')}</TableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && <TableRow><TableCell colSpan={9} align="center">—</TableCell></TableRow>}
              {rows.map((tx) => (
                <TableRow key={tx.uuid} hover>
                  <TableCell>{formatDateTime(tx.occurred_at)}</TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => navigate(`/loyalty/clients/${tx.loyalty_client?.uuid}`)}>
                      {tx.loyalty_client ? `${tx.loyalty_client.first_name} ${tx.loyalty_client.last_name || ''}` : '—'}
                    </Button>
                  </TableCell>
                  <TableCell><Chip size="small" label={tx.type} color={tx.type === 'REFUND' ? 'error' : 'default'} /></TableCell>
                  <TableCell>{tx.location?.name}</TableCell>
                  <TableCell align="right">{formatMoney(tx.purchase_amount)}</TableCell>
                  <TableCell align="right">{tx.points_earned > 0 ? '+' : ''}{formatPoints(tx.points_earned)}</TableCell>
                  <TableCell>{tx.user?.name || '—'}</TableCell>
                  <TableCell>{tx.external_reference || '—'}</TableCell>
                  <TableCell align="right">
                    {tx.type === 'PURCHASE' && hasPermission('loyalty.manage') && (
                      <Button size="small" color="error" onClick={() => refund(tx)}>{t('loyalty.refund')}</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            rowsPerPage={perPage}
            onRowsPerPageChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </TableContainer>
      )}

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default LoyaltyTransactionList;
